// lib/go-prefetch.ts
// Pure decision logic for the hover/focus prefetch on outbound CTAs
// (Priority 320). Only the cloaked affiliate hop (/go, /go/<slug>) is ever
// prefetched — never /reviews, /guides, /categories, /, hash links or an
// external URL. Kept free of React and next/* so it is unit-testable; the
// anchor that calls it is components/public/GoPrefetchAnchor.tsx.
//
// A router prefetch of /go/<slug> is a real GET against the redirect handler,
// so lib/affiliate-redirect.ts uses isRouterPrefetchRequest below to answer
// it without logging a click, minting a session or redirecting.

// Placeholder origin for resolving a relative href. An href that resolves to
// any other origin (https://…, //host, /\host) is external.
const BASE_ORIGIN = "http://go-prefetch.invalid";

/**
 * The path (pathname + query, no fragment) the router may prefetch for this
 * href, or null when the href is not a same-origin /go path.
 */
export function goPrefetchPath(href: string): string | null {
  if (!href.startsWith("/")) return null;
  let url: URL;
  try {
    url = new URL(href, BASE_ORIGIN);
  } catch {
    return null;
  }
  if (url.origin !== BASE_ORIGIN) return null;
  if (url.pathname !== "/go" && !url.pathname.startsWith("/go/")) return null;
  return `${url.pathname}${url.search}`;
}

/** True only for a same-origin /go or /go/<slug> href. Query strings may stay. */
export function shouldPrefetchGoHref(href: string): boolean {
  return goPrefetchPath(href) !== null;
}

/**
 * True when the request is a Next client-router prefetch rather than a
 * visitor's navigation. A CTA click is a plain document request (the anchors
 * are <a target="_blank">, not next/link), so it never carries these headers.
 */
export function isRouterPrefetchRequest(headers: Pick<Headers, "has">): boolean {
  return headers.has("next-router-prefetch") || headers.has("next-router-segment-prefetch");
}
